/**
 * The two-button confirmation dialog — the third member of the `.popover` family.
 *
 * The set is split by **what the dialog is**, not by which props happen to be passed:
 *
 * - `Popover` — a question that collects a value (cancel + confirm around an input).
 * - `ConfirmPopover` — a question with a yes and a no. This one.
 * - `AlertPopover` — an answer. One dismiss action, and **no way to add a confirm**.
 * - `UnsavedChangesPopover` — a question with three answers, one destructive and not the
 *   primary (D247). It is a fourth sibling rather than this component with `secondaryAction`
 *   filled in, for the reason the next paragraph gives: that slot is a retry, not a discard.
 *
 * That last boundary is load-bearing rather than stylistic. D143's refusal must never acquire a
 * proceed affordance, and the reliable way to guarantee that is a component with nowhere to put
 * one — not a shared component with `onConfirm` left undefined, because an undefined prop is
 * something a later caller can define.
 *
 * `secondaryAction` exists for the one case a confirmation genuinely needs a third control: a
 * **retry**, when the dialog is waiting on a read it needs before the confirm means anything. It
 * sits beside cancel rather than beside confirm, because it is a way of *not* proceeding yet.
 *
 * Renders against the same `.popover*` rules in `global.css` as its two siblings, and inherits
 * their z-index note: render as a *sibling* of `<Drawer>`, never a child.
 *
 * **Android hardware back does not close this**, exactly as it does not close the drawer, the
 * member sheet, or either other popover — the shell's router owns history and no overlay in the
 * app intercepts it. Backdrop, cancel, and `Escape` all dismiss. Recorded in
 * `implementation-notes.md` → P7 rather than fixed here: it is one behaviour across every overlay
 * and belongs to P6, whose router and overlay primitives would have to change together.
 */
import { useEffect, useRef, type ReactNode } from 'react'

export function ConfirmPopover({
  title,
  body,
  hint,
  error,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  confirmDisabled = false,
  destructive = true,
  secondaryAction,
}: {
  readonly title: string
  readonly body: ReactNode
  /** A neutral status line — "loading", typically. Not an error. */
  readonly hint?: string
  readonly error?: string
  readonly cancelLabel: string
  readonly confirmLabel: string
  onCancel(): void
  onConfirm(): void
  readonly confirmDisabled?: boolean
  /**
   * **Names the tone, never the shape**, which is why it does not reopen the boundary above: this
   * is still a question with a yes and a no, and `AlertPopover` still has nowhere to put a confirm.
   *
   * `true` by default because every caller before it was destructive — a deletion, a revoke, a
   * removal — and a default that changed would have re-toned all of them silently. `false` is for
   * the confirmation whose *cancel* is the discarding half: the invite screen's address-book
   * leave-confirm, where Add commits what was ticked and Cancel throws it away, so the danger
   * colours on the confirm read backwards.
   */
  readonly destructive?: boolean
  readonly secondaryAction?: { readonly label: string; onClick(): void }
}): JSX.Element {
  const cancelRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    // Focus lands on **cancel**, not confirm. The destructive control must never be one stray
    // Enter away, and this dialog exists to be read before it is answered.
    cancelRef.current?.focus()
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onCancel])

  return (
    <div className="popover-backdrop" onClick={onCancel}>
      <div
        className="popover"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-popover-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="popover-title" id="confirm-popover-title">
          {title}
        </h2>
        <p className="popover-body">{body}</p>
        {hint === undefined ? null : <p className="hint">{hint}</p>}
        {error === undefined ? null : <p className="popover-error">{error}</p>}
        <div className="popover-actions">
          <button ref={cancelRef} type="button" className="btn-secondary" onClick={onCancel}>
            {cancelLabel}
          </button>
          {secondaryAction === undefined ? null : (
            <button type="button" className="btn-secondary" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </button>
          )}
          {/*
            `btn-primary` with the danger colours rather than a new class: this is the same
            treatment `removeMe`'s confirm chip carried as a route, kept so the destructive action
            reads the same after the move. Disabled while the dialog is still waiting on the
            roster — the copy above is not yet known to be true, and the answer decides whether
            the action is even offered.

            The colours come off with `destructive: false`, leaving the plain primary — for a
            confirmation where the *cancel* is the half that throws something away.
          */}
          <button
            type="button"
            className={`btn-primary${destructive ? ' popover-danger' : ''}`}
            disabled={confirmDisabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
